import React, { useState, useEffect } from 'react';
import api from '../utils/api'; 
import { useNotification } from '../context/NotificationContext';
import { useNavigate } from 'react-router-dom';

export default function NotificationsView({ 
  showToast, 
  setCurrentView, 
  setViewedUserId, 
  setNotificationPostId, 
  setNotificationCommentId,
  profile 
}) {  
  const [notifications, setNotifications] = useState([]);
  const { unreadCount, setUnreadCount, setHasUnread } = useNotification();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await api.get('/notifications');
      
      if (res.data && res.data.notifications) {
        setNotifications(res.data.notifications); 
        setUnreadCount(res.data.unread_count);
      } else {
        setNotifications([]); 
      }
    } catch (err) {
      console.error("DEBUG: Request failed:", err);
      showToast("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      
      setUnreadCount(prev => {
        const newCount = Math.max(0, prev - 1);
        if (newCount === 0) setHasUnread(false);
        return newCount;
      });
      
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const markAllRead = async () => {
    if (unreadCount === 0) return;
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      
      setUnreadCount(0);
      setHasUnread(false);
      showToast('All notifications marked as read ✓');
    } catch (err) {
      console.error('Error marking all as read:', err);
      showToast('Failed to mark all as read');
    }
  };

  // 🆕 ADDED: Handler for Accepting Connection
  const handleAcceptConnection = async (notifId, connectionId, e) => {
    e.stopPropagation(); // Prevents the whole notification block from being clicked
    try {
        await api.post(`/connections/accept/${connectionId}`);
        showToast('Connection accepted!');
        markAsRead(notifId); // Mark the request as read automatically
        fetchNotifications(); // Refresh the list
    } catch (err) {
        console.error('Accept error:', err);
        showToast(err.response?.data?.message || 'Failed to accept connection.');
    }
  };

  // 🆕 ADDED: Handler for Declining Connection
  const handleDeclineConnection = async (notifId, connectionId, e) => {
    e.stopPropagation();
    try {
        await api.post(`/connections/decline/${connectionId}`);
        showToast('Connection request declined.');
        markAsRead(notifId);
        fetchNotifications();
    } catch (err) {
        console.error('Decline error:', err);
        showToast(err.response?.data?.message || 'Failed to decline connection.');
    }
  };

  const handleNotificationClick = async (notif) => {
    if (!notif.is_read) {
      markAsRead(notif.id);
    }

    try {
      const myId = profile?.user_id || profile?.id;

      // 1. UPVOTES (Only need the Post ID)
      if (notif.type === 'upvote') {
        const isMyOwnPost = notif.user_id === myId;
        setViewedUserId(isMyOwnPost ? null : notif.user_id);
        setNotificationPostId(notif.reference_id);
        setNotificationCommentId(null); 
        setCurrentView('profile');
      } 
      // 2. COMMENTS AND REPLIES (Need to look up the comment to get the Post ID)
      else if (notif.type === 'comment' || notif.type === 'comment_reply') {
        try {
          const res = await api.get(`/posts/comments/${notif.reference_id}`);
          const commentData = res.data.comment || res.data;
          
          const postId = commentData.post_id;
          const targetUserId = commentData.post_owner_id; 

          if (postId && targetUserId) {
            const isMyOwnPost = targetUserId === myId;
            setViewedUserId(isMyOwnPost ? null : targetUserId);
            setNotificationPostId(postId);
            setNotificationCommentId(notif.reference_id); // Highlights the exact comment
            setCurrentView('profile');
          } else {
            showToast('Could not locate the original post or its owner.');
          }
        } catch (err) {
          // FALLBACK: Handles older 'comment' notifications from before we updated the backend
          if (notif.type === 'comment') {
            const isMyOwnPost = notif.user_id === myId;
            setViewedUserId(isMyOwnPost ? null : notif.user_id);
            setNotificationPostId(notif.reference_id); // In old notifications, this was the postId
            setNotificationCommentId(null); 
            setCurrentView('profile');
          } else {
            showToast('Could not locate the exact comment.');
          }
        }
      } 
      // 3. FOLLOWS & CONNECTIONS
      else if (notif.type === 'follow' || notif.type === 'connection_request' || notif.type === 'connection_accepted') {
        setViewedUserId(notif.actor_id);
        setNotificationPostId(null);
        setNotificationCommentId(null);
        setCurrentView('profile');
      }
    } catch (err) {
      console.error('Navigation error:', err);
      showToast('Could not navigate to the notification target.');
    }
  };

  const timeAgo = (dateString) => {
    const diffHours = Math.abs(new Date() - new Date(dateString)) / 36e5;
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${Math.floor(diffHours)}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  // 🆕 UPDATED: Added configurations for connection notifications
  const getNotificationConfig = (type) => {
    switch (type) {
      case 'follow': return { icon: '+', colorClass: 'var(--teal)', bgClass: 'var(--teal-l)', text: 'started following you' };
      case 'upvote': return { icon: '▲', colorClass: 'var(--amber)', bgClass: 'var(--amber-l)', text: 'upvoted your post' };
      case 'comment': return { icon: '💬', colorClass: 'var(--blue)', bgClass: 'var(--blue-l)', text: 'commented on your post' };
      case 'comment_reply': return { icon: '↩️', colorClass: 'var(--green)', bgClass: 'var(--green-l)', text: 'replied to your comment' };
      case 'connection_request': return { icon: '🤝', colorClass: 'var(--purple)', bgClass: 'var(--purple-l)', text: 'sent you a connection request' };
      case 'connection_accepted': return { icon: '✅', colorClass: 'var(--teal)', bgClass: 'var(--teal-l)', text: 'accepted your connection request' };
      default: return { icon: '🔔', colorClass: 'var(--purple)', bgClass: 'var(--purple-l)', text: 'interacted with you' };
    }
  };

  if (loading) return <div style={{ padding: '20px', color: 'var(--text3)' }}>Loading notifications...</div>;

  if (!notifications || notifications.length === 0) {
    return <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text3)' }}>No new notifications.</div>;
  }

  return (
    <div className="view active" id="view-notifications">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)', fontFamily: "'Syne', sans-serif" }}>
          Notifications {unreadCount > 0 && <span style={{ color: 'var(--purple)', fontSize: '14px' }}>({unreadCount})</span>}
        </div>
        <button 
          className="btn btn-sm" 
          onClick={markAllRead}
          disabled={unreadCount === 0 || loading}
          style={{ opacity: unreadCount === 0 ? 0.5 : 1 }}
        >
          Mark all read
        </button>
      </div>
      
      <div className="notif-panel">
        {notifications.map((notif) => {
          const config = getNotificationConfig(notif.type);
          const isUnread = !notif.is_read;
          
          return (
            <div 
              key={notif.id} 
              className={`notif-item ${isUnread ? 'unread' : ''}`} 
              onClick={() => handleNotificationClick(notif)} 
              style={{ cursor: 'pointer' }} 
            >
              <div className="ni-av-wrap">
                <div 
                  className="ni-av" 
                  style={{ background: config.bgClass, width: '34px', height: '34px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', overflow: 'hidden' }}
                >
                  {notif.actor_avatar ? (
                    <img src={notif.actor_avatar} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    notif.actor_name ? notif.actor_name.charAt(0).toUpperCase() : 'U'
                  )}
                </div>
                <div className="ni-type-icon" style={{ background: config.colorClass }}>
                  {config.icon}
                </div>
              </div>
              
              <div className="ni-text">
                <div className="ni-name">{notif.actor_name || `@${notif.actor_handle}`}</div>
                {/* 🆕 UPDATED: Displays the custom note if it exists in DB message column, otherwise falls back to config text */}
                <div className="ni-desc">{notif.message || config.text}</div>
                <div className="ni-time">{timeAgo(notif.created_at)}</div>
                
                {notif.type === 'follow' && (
                  <div className="ni-actions" style={{ marginTop: '8px' }}>
                    <button 
                      className="ni-accept" 
                      onClick={(e) => {
                        e.stopPropagation(); 
                        setViewedUserId(notif.actor_id);
                        setCurrentView('profile');
                      }}
                    >
                      View Profile
                    </button>
                  </div>
                )}

                {/* 🆕 ADDED: The Accept/Decline action buttons for connection requests */}
                {notif.type === 'connection_request' && (
                  <div className="ni-actions" style={{ }}>
                    <button 
                      className="ni-accept" 
                      onClick={(e) => handleAcceptConnection(notif.id, notif.reference_id, e)}
                      style={{ }}
                    >
                      Accept
                    </button>
                    <button 
                      className="ni-decline" 
                      onClick={(e) => handleDeclineConnection(notif.id, notif.reference_id, e)}
                      style={{ }}
                    >
                      Decline
                    </button>
                  </div>
                )}
              </div>
              
              {isUnread && (
                <div style={{ width: '8px', height: '8px', background: 'var(--purple)', borderRadius: '50%', marginLeft: 'auto', alignSelf: 'center' }}></div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}